import { useEffect, useMemo, useState } from "react";
import {
  Briefcase,
  Eye,
  EyeOff,
  Facebook,
  Image as ImageIcon,
  Instagram,
  Linkedin,
  Loader2,
  Save,
  Search,
  Star,
  X,
} from "lucide-react";
import AdminShell from "../components/AdminShell";
import ImageUploader from "../components/ImageUploader";
import { useI18n } from "../i18n/I18nContext";
import { api } from "../lib/nailApi";

const TOKEN_KEY = "nailEvent.adminToken";

/**
 * Admin → Board of Directors
 *
 * Lists every Speaker row + lets the master admin toggle `is_board_director`
 * on/off (one click), edit board_title / board_order / bio / photo without
 * touching code. Hidden directors are kept in the DB so they can be restored
 * later with a single toggle.
 */
export default function AdminBoardOfDirectors() {
  const { lang } = useI18n();
  const L = (vi, en) => (lang === "en" ? en : vi);
  const jwt = localStorage.getItem("nailViet.adminJWT") || "";
  const legacy = sessionStorage.getItem(TOKEN_KEY) || "";
  const headers = useMemo(
    () => ({
      ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
      ...(legacy ? { "X-Admin-Token": legacy } : {}),
    }),
    [jwt, legacy],
  );

  const [speakers, setSpeakers] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [toast, setToast] = useState("");

  const flash = (m) => {
    setToast(m);
    setTimeout(() => setToast(""), 2200);
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/speakers", { headers, params: q ? { q, limit: 500 } : { limit: 500 } });
      setSpeakers(r.data?.speakers || []);
    } catch (err) {
      flash(err?.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const toggle = async (sp) => {
    setBusyId(sp.id);
    try {
      await api.put(
        `/admin/speakers/${sp.id}`,
        {
          is_board_director: !sp.is_board_director,
          board_title: sp.board_title || null,
          board_order: sp.board_order || 0,
        },
        { headers },
      );
      flash(!sp.is_board_director ? L("Đã hiển thị trên trang chủ", "Now visible on homepage") : L("Đã ẩn khỏi trang chủ", "Hidden from homepage"));
      await load();
    } catch (err) {
      flash(err?.response?.data?.detail || "Error");
    } finally {
      setBusyId(null);
    }
  };

  const save = async (payload, id) => {
    try {
      await api.put(`/admin/speakers/${id}`, payload, { headers });
      flash(L("Đã lưu", "Saved"));
      setEditing(null);
      await load();
    } catch (err) {
      flash(err?.response?.data?.detail || "Error");
    }
  };

  // Directors (visible) first, then everyone else, sorted by board_order then name.
  const sorted = useMemo(() => {
    const arr = [...speakers];
    arr.sort((a, b) => {
      if (a.is_board_director !== b.is_board_director) return a.is_board_director ? -1 : 1;
      if ((a.board_order || 0) !== (b.board_order || 0)) return (a.board_order || 0) - (b.board_order || 0);
      return (a.full_name || "").localeCompare(b.full_name || "");
    });
    return arr;
  }, [speakers]);

  const visibleCount = speakers.filter((s) => s.is_board_director).length;

  return (
    <AdminShell
      testid="admin-board-of-directors"
      title={
        <span className="inline-flex items-center gap-2" data-testid="page-title">
          {L("Hội đồng Quản trị", "Board of Directors")}
          <span className="inline-flex items-center px-2 py-0.5 rounded-full border border-emerald2/30 bg-emerald2/5 text-emerald2 text-[10px] uppercase tracking-[0.18em] font-semibold" data-testid="visible-count">
            {visibleCount} {L("hiển thị", "visible")}
          </span>
        </span>
      }
    >
      <div className="max-w-6xl">
        {/* Search + hint */}
        <div className="bg-white rounded-2xl border border-espresso/10 p-4 md:p-5 mb-6 flex flex-wrap items-center gap-3" data-testid="board-search-bar">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-warmgray-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder={L("Tìm theo tên, chức danh, công ty…", "Search name, title, company…")}
              data-testid="board-search-input"
              className="w-full bg-linen-100/60 border border-espresso/10 rounded-xl pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-espresso"
            />
          </div>
          <button onClick={load} data-testid="board-search-button" className="inline-flex items-center gap-1.5 bg-espresso text-linen text-xs uppercase tracking-[0.18em] font-semibold rounded-full px-4 py-2 hover:bg-espresso-hover">
            {L("Tìm", "Search")}
          </button>
          <p className="text-xs text-warmgray-700 w-full mt-1">
            {L(
              "Bật/Tắt công tắc bên cạnh mỗi người để hiển thị/ẩn họ trên trang chủ. Dữ liệu vẫn được lưu lại — bạn có thể bật lại bất cứ lúc nào.",
              "Toggle the switch beside each person to show/hide them on the homepage. Data is preserved — you can flip it back anytime.",
            )}
          </p>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="py-16 text-center" data-testid="board-loading">
            <Loader2 className="w-6 h-6 animate-spin inline-block text-warmgray-500" />
          </div>
        ) : sorted.length === 0 ? (
          <div className="text-center py-16 text-warmgray-700" data-testid="board-empty">
            {L("Không có diễn giả nào.", "No speakers found.")}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="board-grid">
            {sorted.map((sp) => (
              <Card
                key={sp.id}
                sp={sp}
                busy={busyId === sp.id}
                onToggle={() => toggle(sp)}
                onEdit={() => setEditing(sp)}
                L={L}
              />
            ))}
          </div>
        )}
      </div>

      {editing && (
        <EditDialog
          speaker={editing}
          onClose={() => setEditing(null)}
          onSave={(payload) => save(payload, editing.id)}
          L={L}
        />
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-charcoal text-linen rounded-full px-4 py-2 text-xs font-medium shadow-lg" data-testid="board-toast">
          {toast}
        </div>
      )}
    </AdminShell>
  );
}

function Card({ sp, busy, onToggle, onEdit, L }) {
  const visible = !!sp.is_board_director;
  return (
    <article
      className={`relative rounded-2xl border p-5 transition-all bg-white ${
        visible ? "border-emerald2/30 shadow-[0_6px_30px_-12px_rgba(14,91,79,0.18)]" : "border-warmgray-200"
      }`}
      data-testid={`board-card-${sp.id}`}
    >
      {/* Visibility pill */}
      <div className="absolute top-3 right-3 flex items-center gap-2">
        <span
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.18em] font-semibold border ${
            visible
              ? "bg-emerald2/10 text-emerald2 border-emerald2/30"
              : "bg-warmgray-100 text-warmgray-700 border-warmgray-200"
          }`}
        >
          {visible ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
          {visible ? L("Hiển thị", "Visible") : L("Đã ẩn", "Hidden")}
        </span>
      </div>

      <div className="flex items-start gap-4">
        {sp.photo_url ? (
          <img
            src={sp.photo_url}
            alt={sp.full_name}
            className="w-20 h-20 rounded-xl object-cover bg-warmgray-100"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <div className="w-20 h-20 rounded-xl bg-warmgray-100 flex items-center justify-center text-warmgray-500">
            <ImageIcon className="w-6 h-6" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h3 className="font-serif text-xl text-charcoal truncate">{sp.full_name}</h3>
          {sp.board_title && (
            <p className="text-xs uppercase tracking-[0.18em] font-semibold text-espresso mt-1 truncate" data-testid={`board-title-${sp.id}`}>
              <Star className="w-3 h-3 inline mr-1" />
              {sp.board_title}
            </p>
          )}
          {sp.title && <p className="text-xs text-warmgray-700 mt-1 truncate">{sp.title}</p>}
          {sp.company && (
            <p className="text-xs text-warmgray-500 mt-0.5 truncate inline-flex items-center gap-1">
              <Briefcase className="w-3 h-3" />
              {sp.company}
            </p>
          )}
          {sp.bio && <p className="mt-2 text-xs text-warmgray-700 line-clamp-3">{sp.bio}</p>}
          {(sp.linkedin_url || sp.facebook_url || sp.instagram_url) && (
            <div className="mt-2 flex items-center gap-2">
              {sp.linkedin_url && (
                <a href={sp.linkedin_url} target="_blank" rel="noreferrer" aria-label="LinkedIn" data-testid={`board-card-linkedin-${sp.id}`} className="text-warmgray-500 hover:text-emerald2">
                  <Linkedin className="w-3.5 h-3.5" />
                </a>
              )}
              {sp.facebook_url && (
                <a href={sp.facebook_url} target="_blank" rel="noreferrer" aria-label="Facebook" data-testid={`board-card-facebook-${sp.id}`} className="text-warmgray-500 hover:text-emerald2">
                  <Facebook className="w-3.5 h-3.5" />
                </a>
              )}
              {sp.instagram_url && (
                <a href={sp.instagram_url} target="_blank" rel="noreferrer" aria-label="Instagram" data-testid={`board-card-instagram-${sp.id}`} className="text-warmgray-500 hover:text-emerald2">
                  <Instagram className="w-3.5 h-3.5" />
                </a>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="mt-5 flex items-center justify-between gap-3">
        {/* Toggle switch */}
        <button
          type="button"
          onClick={onToggle}
          disabled={busy}
          aria-pressed={visible}
          data-testid={`board-toggle-${sp.id}`}
          className={`relative inline-flex items-center gap-2 px-1 py-1 rounded-full transition-colors text-xs font-medium ${
            visible ? "bg-emerald2 text-linen" : "bg-warmgray-200 text-warmgray-700"
          } ${busy ? "opacity-60" : ""}`}
        >
          <span
            className={`inline-block w-5 h-5 rounded-full bg-white transition-transform ${
              visible ? "translate-x-7" : "translate-x-0"
            }`}
          />
          <span className="pl-1 pr-3 uppercase tracking-[0.18em] text-[10px]">
            {busy ? "…" : visible ? L("Bật", "On") : L("Tắt", "Off")}
          </span>
        </button>

        <div className="flex items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-warmgray-500" data-testid={`board-order-${sp.id}`}>
            {L("Thứ tự", "Order")}: {sp.board_order || 0}
          </span>
          <button
            type="button"
            onClick={onEdit}
            data-testid={`board-edit-${sp.id}`}
            className="ml-2 inline-flex items-center gap-1 text-xs font-medium text-espresso hover:text-espresso-hover border border-espresso/20 rounded-full px-3 py-1.5"
          >
            {L("Chỉnh sửa", "Edit")}
          </button>
        </div>
      </div>
    </article>
  );
}

function EditDialog({ speaker, onClose, onSave, L }) {
  const [form, setForm] = useState({
    board_title: speaker.board_title || "",
    board_order: speaker.board_order || 0,
    bio: speaker.bio || "",
    photo_url: speaker.photo_url || "",
    full_name: speaker.full_name || "",
    title: speaker.title || "",
    company: speaker.company || "",
    is_board_director: !!speaker.is_board_director,
    linkedin_url: speaker.linkedin_url || "",
    facebook_url: speaker.facebook_url || "",
    instagram_url: speaker.instagram_url || "",
    website_url: speaker.website_url || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onSave({
      board_title: form.board_title || null,
      board_order: parseInt(form.board_order, 10) || 0,
      bio: form.bio || null,
      photo_url: form.photo_url || null,
      full_name: form.full_name.trim(),
      title: form.title || null,
      company: form.company || null,
      is_board_director: form.is_board_director,
      linkedin_url: form.linkedin_url || null,
      facebook_url: form.facebook_url || null,
      instagram_url: form.instagram_url || null,
      website_url: form.website_url || null,
    });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[70] bg-charcoal/40 backdrop-blur-sm flex items-end md:items-center justify-center px-3 md:px-6 py-4 md:py-10" data-testid="board-edit-dialog">
      <form onSubmit={submit} className="w-full max-w-2xl bg-white rounded-3xl shadow-[0_30px_80px_-30px_rgba(0,0,0,0.5)] max-h-[92vh] flex flex-col">
        <div className="px-6 md:px-8 py-5 border-b border-warmgray-200 flex items-center justify-between">
          <h3 className="font-serif text-2xl md:text-3xl text-charcoal">
            {L("Chỉnh sửa Thành viên Hội đồng", "Edit Board Member")}
          </h3>
          <button type="button" onClick={onClose} data-testid="board-edit-close" className="text-warmgray-500 hover:text-charcoal">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 md:px-8 py-6 overflow-y-auto grid md:grid-cols-2 gap-4">
          <Input label={L("Tên đầy đủ *", "Full name *")} value={form.full_name} onChange={(v) => set("full_name", v)} required testId="board-edit-full-name" />
          <Input label={L("Chức danh trong Hội đồng", "Board title")} value={form.board_title} onChange={(v) => set("board_title", v)} placeholder={L("VD: Chủ tịch, Cố vấn…", "e.g. Chair, Advisor…")} testId="board-edit-board-title" />
          <Input label={L("Chức danh nghề nghiệp", "Professional title")} value={form.title} onChange={(v) => set("title", v)} testId="board-edit-title" />
          <Input label={L("Công ty", "Company")} value={form.company} onChange={(v) => set("company", v)} testId="board-edit-company" />
          <div className="md:col-span-2">
            <ImageUploader
              label={L("Ảnh đại diện", "Photo")}
              value={form.photo_url}
              onChange={(url) => set("photo_url", url)}
              subfolder="board"
              aspect="square"
              testId="board-edit-photo"
              hint={L("Ảnh chân dung vuông hiển thị đẹp nhất trên trang chủ. Tối đa 10MB.", "Square headshot works best on homepage. Max 10MB.")}
            />
          </div>
          <Textarea label={L("Tiểu sử", "Bio")} value={form.bio} onChange={(v) => set("bio", v)} rows={5} testId="board-edit-bio" className="md:col-span-2" />

          {/* Phase 7.8.1 — Social profile links */}
          <Input label="LinkedIn URL" value={form.linkedin_url} onChange={(v) => set("linkedin_url", v)} placeholder="https://www.linkedin.com/in/…" testId="board-edit-linkedin" className="md:col-span-2" />
          <Input label="Facebook URL" value={form.facebook_url} onChange={(v) => set("facebook_url", v)} placeholder="https://www.facebook.com/…" testId="board-edit-facebook" />
          <Input label="Instagram URL" value={form.instagram_url} onChange={(v) => set("instagram_url", v)} placeholder="https://www.instagram.com/…" testId="board-edit-instagram" />
          <Input label="Website URL" value={form.website_url} onChange={(v) => set("website_url", v)} placeholder="https://…" testId="board-edit-website" className="md:col-span-2" />

          <div className="grid grid-cols-2 gap-4 md:col-span-2">
            <Input
              label={L("Thứ tự hiển thị (số nhỏ trước)", "Display order (lower first)")}
              value={form.board_order}
              onChange={(v) => set("board_order", v)}
              type="number"
              testId="board-edit-order"
            />
            <label className="flex items-end gap-2 pb-2">
              <input
                type="checkbox"
                checked={form.is_board_director}
                onChange={(e) => set("is_board_director", e.target.checked)}
                data-testid="board-edit-visible"
                className="w-5 h-5 accent-emerald2"
              />
              <span className="text-sm text-charcoal">
                {L("Hiển thị trên trang chủ", "Show on homepage")}
              </span>
            </label>
          </div>
        </div>
        <div className="px-6 md:px-8 py-4 border-t border-warmgray-200 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-warmgray-700 hover:text-charcoal" data-testid="board-edit-cancel">
            {L("Hủy", "Cancel")}
          </button>
          <button type="submit" disabled={!form.full_name?.trim() || saving} data-testid="board-edit-save" className="inline-flex items-center gap-2 bg-espresso text-linen rounded-full px-5 py-2 text-sm font-medium hover:bg-espresso-hover disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {L("Lưu", "Save")}
          </button>
        </div>
      </form>
    </div>
  );
}

function Input({ label, value, onChange, placeholder, testId, required, type = "text", className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-[10px] uppercase tracking-[0.22em] font-semibold text-warmgray-700 mb-1.5">{label}</span>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        data-testid={testId}
        className="w-full bg-white border border-warmgray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald2/40"
      />
    </label>
  );
}
function Textarea({ label, value, onChange, rows = 3, testId, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-[10px] uppercase tracking-[0.22em] font-semibold text-warmgray-700 mb-1.5">{label}</span>
      <textarea
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        data-testid={testId}
        className="w-full bg-white border border-warmgray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald2/40"
      />
    </label>
  );
}
